import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { Text, Circle, Group, Layer, Line, Rect, Stage } from "react-konva";
import Konva from "konva";

class Point {
  x: number;
  y: number;
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
  distance(p: Point) {
    return Math.sqrt((this.x - p.x) ** 2 + (this.y - p.y) ** 2);
  }
}

class Vector {
  x: number;
  y: number;
  constructor(x, y) {
    this.x = x / Math.sqrt(x ** 2 + y ** 2);
    this.y = y / Math.sqrt(x ** 2 + y ** 2);
  }
}

class Edge {
  start: Point;
  end: Point;
  weight: number;
  constructor(start, end, weight) {
    this.start = start;
    this.end = end;
    this.weight = weight;
  }
}

const maxX = 500;
const maxY = 400;

const pointA = new Point(-260, 250);
const pointB = new Point(-300, 0);
const pointC = new Point(0, 0);
const pointD = new Point(0, -250);
const pointE = new Point(300, -150);
const pointF = new Point(150, 200);

const pointHighlight = (step, i) => {
  if (step == 1 && i == 0) return true;
  else if (step == 2 && (i == 0 || i == 1)) return true;
  else if (step == 3 && (i == 0 || i == 5)) return true;
  else if (step == 4 && (i == 1 || i == 2)) return true;
  else if (step == 5 && (i == 1 || i == 3)) return true;
  else if (step == 6 && (i == 2 || i == 3)) return true;
  else if (step == 7 && (i == 2 || i == 4)) return true;
  else if (step == 8 && (i == 4 || i == 5)) return true;
  else if (step == 9 && (i == 3 || i == 4)) return true;
  else if (step == 10 && i == 4) return true;
  else return false;
};

const edgeHighlight = (step, i) => {
  if (step == 2 && i == 0) return true;
  else if (step == 3 && i == 7) return true;
  else if (step == 4 && i == 1) return true;
  else if (step == 5 && i == 3) return true;
  else if (step == 6 && i == 2) return true;
  else if (step == 7 && i == 5) return true;
  else if (step == 8 && i == 6) return true;
  else if (step == 9 && i == 4) return true;
  else return false;
};

function Main() {
  const [points, setPoints] = useState<Point[]>([
    pointA,
    pointB,
    pointC,
    pointD,
    pointE,
    pointF,
  ]);
  const [edges, setEdges] = useState<Edge[]>([
    new Edge(pointA, pointB, 2),
    new Edge(pointB, pointC, 2),
    new Edge(pointC, pointD, 1),
    new Edge(pointB, pointD, 4),
    new Edge(pointD, pointE, 1),
    new Edge(pointC, pointE, 3),
    new Edge(pointF, pointE, 3),
    new Edge(pointF, pointA, 4),
  ]);
  const [mousePoint, setMousePoint] = useState(new Point(-1, -1));
  const divRef = useRef(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const groupRef: React.RefObject<Konva.Group> = useRef();

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
    }
  }, [divRef]);
  const [showDistance, setShowDistance] = useState(false);

  const cleanAllPoints = () => {
    setPoints([]);
  };

  const [step, setStep] = useState(0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 8 }}>
        <Controls step={step} setStep={setStep} />
      </Box>
      <Box
        sx={{
          flex: 12,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          ></Box>
        </Box>
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          scaleY={-1}
          offsetX={-dimensions.width / 2}
          offsetY={dimensions.height / 2}
          onMouseMove={() => {
            let pos = groupRef.current.getRelativePointerPosition();
            setMousePoint(new Point(pos.x, pos.y));
          }}
          onMouseLeave={() => {
            setMousePoint(new Point(-10, -10));
          }}
        >
          <Layer>
            <Group ref={groupRef}>
              {edges.map((e, i) => {
                let startx = (e.start.x * dimensions.width) / (2 * maxX);
                let starty = (e.start.y * dimensions.height) / (2 * maxY);
                let endx = (e.end.x * dimensions.width) / (2 * maxX);
                let endy = (e.end.y * dimensions.height) / (2 * maxY);
                let normalVector = new Vector(starty - endy, endx - startx);
                return (
                  <Group key={"edge" + i}>
                    <Line
                      points={[startx, starty, endx, endy]}
                      strokeWidth={5}
                      stroke={edgeHighlight(step, i) ? "red" : "black"}
                    />
                    <Text
                      scaleY={-1}
                      fontSize={25}
                      align="center"
                      verticalAlign="middle"
                      fill={edgeHighlight(step, i) ? "red" : "black"}
                      fillAfterStrokeEnabled={true}
                      width={50}
                      height={50}
                      x={(startx + endx) / 2 - 25 + 25 * normalVector.x}
                      y={(starty + endy) / 2 + 25 + 25 * normalVector.y}
                      text={e.weight.toString()}
                    />
                  </Group>
                );
              })}
              {points.map((p, i) => (
                <Group key={"point" + i}>
                  <Circle
                    radius={25}
                    strokeWidth={5}
                    stroke={pointHighlight(step, i) ? "red" : "black"}
                    fill="white"
                    x={(p.x * dimensions.width) / (2 * maxX)}
                    y={(p.y * dimensions.height) / (2 * maxY)}
                  />
                  <Text
                    scaleY={-1}
                    fontSize={25}
                    align="center"
                    verticalAlign="middle"
                    fill={pointHighlight(step, i) ? "red" : "black"}
                    fillAfterStrokeEnabled={true}
                    width={50}
                    height={50}
                    x={(p.x * dimensions.width) / (2 * maxX) - 25}
                    y={(p.y * dimensions.height) / (2 * maxY) + 25}
                    text={(i + 10).toString(36).toUpperCase()}
                  />
                </Group>
              ))}
            </Group>
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function Dijkstras(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Dijstra's Algorithm</title>
        <meta
          name="description"
          content="Explore interactive visualization and tutorial of Dijkstra's Algorithm: Learn how Dijkstra's Algorithm works with a complete explanation and step-by-step tutorial. Visualize table changes and graph highlights to understand the algorithm's process. Perfect for learning and mastering efficient pathfinding techniques in robotics, computer science and mathematics."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default Dijkstras;
